import { DOCUMENT } from '@angular/common';
import {
  Component, ElementRef, Inject, OnDestroy,
  OnInit,
  Renderer2
} from '@angular/core';
import { NavigationEnd, NavigationStart, ResolveStart, Router } from '@angular/router';
import cssVars from 'css-vars-ponyfill';
import { filter, fromEvent, map, Observable, Subscription } from 'rxjs';
import { take } from 'rxjs/internal/operators/take';
import { environment } from 'src/environments/environment';
import { AuthService } from './auth/services/auth.service';
import { ProgresoTimeoutService } from './auth/services/timeout-service';
import { RouteLogService } from './core/services/route-log.service';
import { CompanyStyling } from './modules/framework/settings/models/CompanyStyling';
import { CompanyStylingSettingsService } from './modules/framework/settings/services/company-styling-settings.service';
import { LabelService } from './shared/framework/labels/services/label.service';
import { IframeService } from './shared/iframe/services/iframe.service';
import { CapitalizeFirstLetterPipe } from './shared/pipes/pipes/capitalize-first-letter.pipe';
import { DataUrlPipe } from './shared/pipes/pipes/data-url.pipe';
import { SafeStylePipe } from './shared/pipes/pipes/safe-style.pipe';
@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.css'],
  providers: [SafeStylePipe, DataUrlPipe, CapitalizeFirstLetterPipe],
})
export class AppComponent implements OnInit, OnDestroy {
  private _subscriptions: Subscription[] = [];
  private readonly embeddedResizeMessageType = 'progreso-iframe:height';
  private readonly embeddedResizeRequestMessageType = 'progreso-iframe:request-height';
  private readonly legacyCandidateFormResizeRequestMessageType = 'candidate-form:request-height';
  private embeddedResizeObserver?: ResizeObserver;
  private embeddedResizeAnimationFrameId?: number;
  private embeddedResizeCleanup?: () => void;

  resizeObservable$: Observable<Event>;
  clickObservable$: Observable<Event>;

  constructor(
    private elementRef: ElementRef,
    private _timeoutService: ProgresoTimeoutService,
    private _authService: AuthService,
    private _CompanyStylingSettingsService: CompanyStylingSettingsService,
    private _dataPipe: DataUrlPipe,
    private _capitalizeFirstLetterPipe: CapitalizeFirstLetterPipe,
    private iframeService: IframeService,
    private router: Router,
    private _routeLogService: RouteLogService,
    private _labelService: LabelService,
    private renderer: Renderer2,
    @Inject(DOCUMENT) private document: Document
  ) {
    // Only set timeout for production builds
    if (environment.production) {
      this._subscriptions.push(
        this._timeoutService.startWatching(3600).subscribe((res) => {
          const topDocument = this.getAccessibleTopDocument();

          if (res && topDocument) {
            if (this.embeddedSessionExceptionActive(topDocument.body)) {
              console.info('Session extended: active embedded page');
            } else {
              this._subscriptions.push(
                _authService.isLoggedIn().subscribe((isLoggedIn) => {
                  if (isLoggedIn) {
                    console.info('session expired');
                    this._authService.logOff();
                  }
                })
              );
            }
          }
        })
      );
    }

    this._subscriptions.push(
      this.router.events.pipe(
        filter((event) => event instanceof ResolveStart),
        map((event: ResolveStart) => {
          let data = null;
          let route = event['state'].root;

          while (route) {
            data = route.data || data;
            route = route.firstChild;
          }

          return data;
        })
      ).subscribe(data => {
        if (data.hasOwnProperty('showLayout') && data['showLayout']) {
          this.renderer.addClass(this.document.body, 'showLayout');
        } else {
          this.renderer.removeClass(this.document.body, 'showLayout');
        }
      }));

    router.events.forEach((event) => {
      if (event instanceof NavigationStart) {
        if (
          router.url !== 'undefined' &&
          router.url !== '/login' &&
          router.url.toLowerCase().indexOf('passwordforget') == -1 &&
          router.url.toLowerCase().indexOf('resetpassword') == -1 &&
          router.url.toLowerCase().indexOf('loginforget') === -1 &&
          router.url !== '/' &&
          router.url !== ''
        ) {
          _routeLogService.LogClientRoute(router.url);
        }
      }

      if (event instanceof NavigationEnd) {
        [500, 800, 1500, 2000].forEach(delay => {
          setTimeout(() => {
            this.resize();
            window.parent.postMessage({
              action: 'iframeHeight',
              height: Math.max(document.body.scrollHeight, document.body.offsetHeight, document.documentElement?.offsetHeight, document.documentElement?.clientHeight),
              url: window.location.href
            }, '*');
            this.postEmbeddedIframeHeight();
          }, delay);
        });
      }
    });
  }

  private reviewFrameActive(body): boolean {
    return this.iframeRouteActive(body, ['review']);
  }

  private embeddedPublicCandidatePageActive(): boolean {
    const currentUrl = window.location.href.toLowerCase();

    if (window.self === window.top) {
      return false;
    }

    return currentUrl.indexOf('/e2r/candidate-form') > -1 ||
      currentUrl.indexOf('/e2r/candidate-login') > -1 ||
      currentUrl.indexOf('/e2r/candidate-forgot-password') > -1 ||
      currentUrl.indexOf('/e2r/candidate-set-password') > -1 ||
      currentUrl.indexOf('/e2r/employees/my-candidate-files') > -1 ||
      currentUrl.indexOf('/e2r/signin-with-linkedin') > -1;
  }

  private getAccessibleTopDocument(): Document | null {
    try {
      return window.top?.document ?? null;
    } catch {
      return null;
    }
  }

  private embeddedSessionExceptionActive(body): boolean {
    return this.reviewFrameActive(body) || this.embeddedPublicCandidatePageActive();
  }

  private iframeRouteActive(body, routeFragments: string[]): boolean {
    if (!body) {
      return false;
    }

    const iframes: any = body.getElementsByTagName('iframe');
    for (const iframe of iframes) {
      if (
        iframe &&
        iframe.contentDocument &&
        iframe.contentDocument.readyState === 'complete'
      ) {
        const iframeUrl = iframe.contentDocument.URL?.toLowerCase() ?? '';
        if (routeFragments.some(fragment => iframeUrl.indexOf(fragment.toLowerCase()) > -1)) {
          return true;
        }

        if (this.iframeRouteActive(iframe.contentDocument.body, routeFragments)) {
          return true;
        }
      }
    }

    return false;
  }

  private courseCatalogFrameActive(body): boolean {
    if (!body) {
      return false;
    }

    const iframes: any = body.getElementsByTagName('iframe');
    for (const iframe of iframes) {
      if (
        iframe &&
        iframe.contentDocument &&
        iframe.contentDocument.readyState === 'complete'
      ) {
        if (iframe.contentDocument.URL.indexOf('courses/coursecatalog.asp') > -1) {
          return true;
        }

        if (this.courseCatalogFrameActive(iframe.contentDocument.body)) {
          return true;
        }
      }
    }

    return false;
  }

  private resize() {
    setTimeout(() => {
      try {
        if (!this.courseCatalogFrameActive) {
          (<any>window.top).initIframeResize();
        }
      } catch (e) { }
    });
  }

  ngOnInit(): void {
    if (this.embeddedPublicCandidatePageActive()) {
      this.startEmbeddedIframeResize();
    }

    this._subscriptions.push(
      this._authService
        .isLoggedIn()
        .pipe(take(2))
        .subscribe((loggedOn: boolean) => {
          if (loggedOn) {
            this._subscriptions.push(
              this._authService.getToken().subscribe(() => {
                this._subscriptions.push(
                  this._CompanyStylingSettingsService
                    .getCompanyStyling(true)
                    .subscribe((styling: CompanyStyling) => {
                      this.setStyling(styling);
                    })
                );
              })
            );
            setTimeout(() => {
              const topDocument = this.getAccessibleTopDocument();

              if (topDocument) {
                this.iframeService.findAndRegisterFrames(topDocument.body);
              }
            });
          }
        })
    );

    if (this._authService.isAuthenticated()) {
      this._subscriptions.push(
        this._labelService.switchContext('general').subscribe()
      );
    }

    this.resizeObservable$ = fromEvent(window, 'resize');
    this.clickObservable$ = fromEvent(window, 'click');
    this._subscriptions.push(
      this.resizeObservable$.subscribe((evt) => {
        this.resize();
      })
    );
    this._subscriptions.push(
      this.clickObservable$.subscribe((evt) => {
        this.resize();
      })
    );

  }

  ngOnDestroy(): void {
    this._subscriptions.forEach(sub => sub.unsubscribe());
    this.embeddedResizeCleanup?.();

  }

  private startEmbeddedIframeResize(): void {
    this.document.documentElement.style.overflow = 'hidden';
    this.document.body.style.overflow = 'hidden';

    const onParentResizeRequest = (event: MessageEvent) => {
      if (
        event.data?.type !== this.embeddedResizeRequestMessageType &&
        event.data?.type !== this.legacyCandidateFormResizeRequestMessageType
      ) {
        return;
      }

      this.scheduleEmbeddedIframeHeight();
    };

    const onWindowResize = () => this.scheduleEmbeddedIframeHeight();

    window.addEventListener('message', onParentResizeRequest);
    window.addEventListener('resize', onWindowResize);

    if (typeof ResizeObserver !== 'undefined') {
      this.embeddedResizeObserver = new ResizeObserver(() => this.scheduleEmbeddedIframeHeight());
      this.embeddedResizeObserver.observe(this.document.documentElement);
      this.embeddedResizeObserver.observe(this.document.body);
    }

    this.embeddedResizeCleanup = () => {
      window.removeEventListener('message', onParentResizeRequest);
      window.removeEventListener('resize', onWindowResize);
      this.embeddedResizeObserver?.disconnect();

      if (this.embeddedResizeAnimationFrameId != null) {
        cancelAnimationFrame(this.embeddedResizeAnimationFrameId);
      }
    };

    this.scheduleEmbeddedIframeHeight();
  }

  private scheduleEmbeddedIframeHeight(): void {
    if (this.embeddedResizeAnimationFrameId != null) {
      cancelAnimationFrame(this.embeddedResizeAnimationFrameId);
    }

    this.embeddedResizeAnimationFrameId = requestAnimationFrame(() => {
      this.embeddedResizeAnimationFrameId = undefined;
      this.postEmbeddedIframeHeight();
    });
  }

  private postEmbeddedIframeHeight(): void {
    if (window.self === window.top) {
      return;
    }

    window.parent.postMessage({
      type: this.embeddedResizeMessageType,
      height: Math.max(
        this.document.body?.scrollHeight ?? 0,
        this.document.body?.offsetHeight ?? 0,
        this.document.documentElement?.scrollHeight ?? 0,
        this.document.documentElement?.offsetHeight ?? 0,
        this.document.documentElement?.clientHeight ?? 0
      ),
      url: window.location.href
    }, '*');
  }

  private setStyling(styling: CompanyStyling) {
    if (styling == null) {
      return;
    }

    if (styling.primaryColor) {
      this.elementRef.nativeElement.style.setProperty(
        '--progreso-primary',
        styling.primaryColor ? styling.primaryColor : ''
      );
    }

    if (styling.secondaryColor) {
      this.elementRef.nativeElement.style.setProperty(
        '--progreso-secondary',
        styling.secondaryColor
      );
    }
    if (styling.accentColor) {
      this.elementRef.nativeElement.style.setProperty(
        '--progreso-accent',
        styling.accentColor
      );
    }
    if (styling.accentHoverColor) {
      this.elementRef.nativeElement.style.setProperty(
        '--progreso-accent-hover',
        styling.accentHoverColor
      );
    }
    window.setTimeout(() => {
      if (styling.fileData != null && styling.fileData.length > 0) {
        this.elementRef.nativeElement.style.setProperty(
          '--progreso-logo',
          this.getBackgroundImage(styling)
        );

        cssVars({
          variables: {
            '--progreso-primary': styling.primaryColor
              ? styling.primaryColor
              : '',
            '--progreso-secondary': styling.secondaryColor,
            '--progreso-accent': styling.accentColor,
            '--progreso-accent-hover': styling.accentHoverColor,
            '--progreso-logo': this.getBackgroundImage(styling),
          },
        });
      } else {
        cssVars({
          variables: {
            '--progreso-primary': styling.primaryColor
              ? styling.primaryColor
              : '',
            '--progreso-secondary': styling.secondaryColor
              ? styling.secondaryColor
              : '',
            '--progreso-accent': styling.accentColor ? styling.accentColor : '',
            '--progreso-accent-hover': styling.accentHoverColor
              ? styling.accentHoverColor
              : '',
          },
        });
      }
    }, 1000);
  }

  private getBackgroundImage(companyStyling: CompanyStyling): any {
    if (companyStyling.fileData !== null && companyStyling.fileSize !== null) {
      return 'url(' + this._dataPipe.transform(companyStyling) + ')';
    }
    return '';
  }
}
